// ============================================================================
// WizardContainerInner ViewModel
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import type { FeatureCollection } from "geojson";
import { convertFilesIntoBuffer, GeoData } from "@rogoag/shared-ts";
import { Job, type Deal } from "../../../../../../models";
import { mergeJobs as mergeJobsUtil } from "../../../../../../utilities/mergeJobs";
import type { MergeResolutions } from "../../../../../../utilities/mergeJobs.types";
import { generateUUID } from "../../../../../../utilities/utils";
import { GroupedGeoJSON, GeoJSONOptionsGroup, GeoJSONDataMap } from "../../../../../../utilities/types";
import { CREATE_NEW_SALESPERSON } from "../../../../../../utilities/constants";
import { Oops } from "../../../../../../utilities/oops";
import {
	extractAllFileNames,
	detectUnsupportedFiles,
	categorizeGeoDataResult,
	FileValidationWarning,
	isShapefile,
} from "../../components/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers";
import { useWizardContext } from "../WizardContext";
import type { WizardStep, GeoType, GeodataGroup, UploadedFileInfo } from "./WizardContainer.types";
import type { MissingInvalidDataActions } from "../steps/MissingInvalidData";
import type { PropertyMappings } from "../../../../shared/PropertyMappings.types";
import type {
	WizardContainerInnerProps,
	WizardContainerInnerViewModel,
	WizardIssue,
} from "./WizardContainerInner.types";
import type { InvalidDataIssue } from "./WizardContainer.types";
import { getStepIssueCount, shouldShowStep, isStepResolved, getUnassignedJobCount } from "../hooks/useWizardReducer";
import { createJobsFromWizard } from "../steps/GeoTypeSelection/createJobsFromWizard";
import { extractJobFieldsFromBoundary } from "../../../../shared/extractJobFieldsFromBoundary";
import { toast } from "react-toastify";

/**
 * ViewModel hook for WizardContainerInner component
 */
export function useWizardContainerInnerViewModel(props: WizardContainerInnerProps): WizardContainerInnerViewModel {
	const { onComplete } = props;
	const { state, dispatch, stepConfigs, canProceedToNextStep } = useWizardContext();

	// State for MissingInvalidData actions (exposed for bottom bar)
	const [invalidDataActions, setInvalidDataActions] = useState<MissingInvalidDataActions | null>(null);

	// State for tracking job creation progress (geo-type-selection step)
	const [isCreatingJobs, setIsCreatingJobs] = useState(false);

	// Map of temp salesperson IDs to their data (name/phone), used to resolve temp IDs to newSalesperson on jobs
	const [newSalespersonData, setNewSalespersonData] = useState<Map<string, { name: string; phone: string }>>(new Map());

	// Get visible steps (steps with issues + summary)
	const visibleSteps = useMemo(
		() => stepConfigs.filter((c) => shouldShowStep(state, c.step)).map((c) => c.step),
		[stepConfigs, state],
	);

	// Step issue counts for progress display
	const stepIssueCounts = useMemo(
		() =>
			stepConfigs.reduce(
				(acc, config) => {
					acc[config.step] = getStepIssueCount(state, config.step);
					return acc;
				},
				{} as Record<WizardStep, number>,
			),
		[stepConfigs, state],
	);

	// Calculate which steps are resolved (had issues initially, now resolved)
	const resolvedSteps = useMemo(
		() => new Set(stepConfigs.map((c) => c.step).filter((step) => isStepResolved(state, step))),
		[stepConfigs, state],
	);

	// Check if we're on the first/last step
	const currentStepIndex = visibleSteps.indexOf(state.currentStep);
	const isFirstStep = currentStepIndex === 0;
	const isLastStep = state.currentStep === "summary";

	// Get current step's issues for bottom bar navigation
	const currentStepIssues = useMemo((): WizardIssue[] => {
		switch (state.currentStep) {
			case "duplicate-boundaries":
				return state.issues.duplicateBoundaries;
			case "overlapping-points":
				return state.issues.overlappingPoints;
			case "overlapping-boundaries":
				return state.issues.overlaps;
			case "overlapping-zones":
				return state.issues.overlappingZones;
			case "unmatched-features":
				return state.issues.unmatched;
			case "missing-features":
				return state.issues.missingFeatures;
			case "missing-invalid-data":
				return state.issues.invalidData;
			default:
				return [];
		}
	}, [state.currentStep, state.issues]);

	const currentIssue = currentStepIssues[state.currentIssueIndex];
	const isCurrentIssueSkipped = currentIssue ? state.skippedIssueIds.has(currentIssue.id) : false;
	const unskippedCount = currentStepIssues.filter((i) => !state.skippedIssueIds.has(i.id)).length;

	// Check if an invalidData issue has a missing grower (unskippable)
	const issueHasMissingGrower = (issue: WizardIssue): boolean =>
		"errors" in issue && "grower" in (issue as InvalidDataIssue).errors;

	const canSkipCurrentIssue = currentIssue ? !issueHasMissingGrower(currentIssue) : false;
	const skippableUnskippedCount = currentStepIssues.filter(
		(i) => !state.skippedIssueIds.has(i.id) && !issueHasMissingGrower(i),
	).length;

	// Auto-advance past issue-based steps when all issues on that step are resolved.
	// Only fires if the step originally had issues (stepsWithInitialIssues) — if a step
	// never had issues, it shows its "no issues" placeholder and the user advances manually.
	const ISSUE_BASED_AUTO_ADVANCE_STEPS: WizardStep[] = [
		"unmatched-features",
		"duplicate-boundaries",
		"overlapping-points",
		"overlapping-boundaries",
		"overlapping-zones",
		"missing-features",
		"missing-invalid-data",
	];
	useEffect(() => {
		if (
			ISSUE_BASED_AUTO_ADVANCE_STEPS.includes(state.currentStep) &&
			state.stepsWithInitialIssues.has(state.currentStep) &&
			!state.isValidating &&
			!state.manualNavigation &&
			currentStepIssues.length === 0
		) {
			dispatch({ type: "NEXT_STEP" });
		}
	}, [
		state.currentStep,
		state.stepsWithInitialIssues,
		state.isValidating,
		state.manualNavigation,
		currentStepIssues.length,
		dispatch,
	]);

	// Unified navigation logic
	const isFirstIssue = state.currentIssueIndex === 0;
	const isLastIssue = currentStepIssues.length > 0 ? state.currentIssueIndex >= currentStepIssues.length - 1 : true;

	// Block Next when current issue has validation errors (missing-invalid-data step only)
	const currentIssueHasValidationErrors =
		state.currentStep === "missing-invalid-data" &&
		currentIssue != null &&
		!isCurrentIssueSkipped &&
		invalidDataActions?.hasValidationErrors === true;

	// For other issue-based steps: block if current issue exists and isn't skipped
	// (missing-invalid-data excluded — it uses form validation state instead,
	//  so that fixing errors enables Next to auto-save even though the issue
	//  still exists in the list until save completes)
	const ISSUE_BASED_STEPS: WizardStep[] = [
		"duplicate-boundaries",
		"overlapping-points",
		"overlapping-boundaries",
		"overlapping-zones",
		"unmatched-features",
		"missing-features",
	];
	const currentIssueIsUnresolved =
		ISSUE_BASED_STEPS.includes(state.currentStep) && currentIssue != null && !isCurrentIssueSkipped;

	// Next button enabled when:
	// 1. No validation errors on current issue (missing-invalid-data), AND
	// 2. Current issue is not unresolved (duplicate/overlap/unmatched), AND
	// 3. Not on last issue (can cycle forward), OR on last issue AND can proceed to next step
	const canClickNext =
		currentIssueHasValidationErrors || currentIssueIsUnresolved ? false : !isLastIssue || canProceedToNextStep;

	// Previous button enabled when:
	// 1. Not on first issue (can cycle backward), OR
	// 2. On first issue AND not on first step
	const canClickPrevious = !isFirstIssue || !isFirstStep;

	// Disabled reason - compute based on current step
	const disabledReason = useMemo((): string | undefined => {
		// For missing-invalid-data step, block when current issue has validation errors
		if (
			state.currentStep === "missing-invalid-data" &&
			currentIssue != null &&
			!isCurrentIssueSkipped &&
			invalidDataActions?.hasValidationErrors
		) {
			return "Fix validation errors on this job before continuing";
		}

		// For missing-invalid-data step, block when any issue has a missing grower
		if (state.currentStep === "missing-invalid-data" && currentIssue && issueHasMissingGrower(currentIssue)) {
			return "Grower Name is required — fix this job before continuing";
		}

		// For other issue-based steps, block when current issue is unresolved
		if (currentIssueIsUnresolved) {
			return "Resolve or ignore this issue before continuing";
		}

		// For geo-type-selection step, compute validation reason
		if (state.currentStep === "geo-type-selection") {
			const { geodataGroups, geoTypeSelections, excludedGroupIds } = state.configuration;
			const activeGroups = geodataGroups.filter((g) => !excludedGroupIds.has(g.id));

			if (activeGroups.length === 0) {
				return "No active groups. Include at least one group.";
			}

			const unassignedCount = activeGroups.filter((g) => geoTypeSelections[g.id] == null).length;
			if (unassignedCount > 0) {
				return `${unassignedCount} group${unassignedCount > 1 ? "s" : ""} still need a geo-type assigned.`;
			}

			const hasBoundary = activeGroups.some((g) => geoTypeSelections[g.id] === "Boundary");
			if (!hasBoundary) {
				return "At least one group must be assigned as Boundaries.";
			}

			return undefined;
		}

		// For branch-configuration step
		if (state.currentStep === "branch-configuration") {
			const unassigned = getUnassignedJobCount(state, "branch");
			if (unassigned > 0) {
				return `${unassigned} job${unassigned > 1 ? "s" : ""} still need a branch assigned.`;
			}
			return undefined;
		}

		// For deal-configuration step
		if (state.currentStep === "deal-configuration") {
			const unassigned = getUnassignedJobCount(state, "deal");
			if (unassigned > 0) {
				return `${unassigned} job${unassigned > 1 ? "s" : ""} still need a configuration assigned.`;
			}
			return undefined;
		}

		// For salesperson-assignment step (only when required)
		if (state.currentStep === "salesperson-assignment" && state.isSalespersonRequired) {
			const unassigned = getUnassignedJobCount(state, "salesperson");
			if (unassigned > 0) {
				return `${unassigned} job${unassigned > 1 ? "s" : ""} still need a salesperson assigned.`;
			}
			return undefined;
		}

		// For other steps, use issue-based reasoning
		return !canClickNext ? "Fix or skip all issues before continuing" : undefined;
	}, [
		state.currentStep,
		state.configuration,
		state,
		canClickNext,
		invalidDataActions,
		isCurrentIssueSkipped,
		currentIssueIsUnresolved,
	]);

	// GeoTypeSelection status for bottom bar chips
	const geoTypeStatus = useMemo(() => {
		if (state.currentStep !== "geo-type-selection") {
			return undefined;
		}
		const { geodataGroups, geoTypeSelections, excludedGroupIds } = state.configuration;
		const activeGroups = geodataGroups.filter((g) => !excludedGroupIds.has(g.id));
		const assignedCount = activeGroups.filter((g) => geoTypeSelections[g.id] != null).length;
		const unassignedCount = activeGroups.length - assignedCount;
		return {
			assignedCount,
			totalActiveGroups: activeGroups.length,
			excludedCount: excludedGroupIds.size,
			unassignedCount,
		};
	}, [state.currentStep, state.configuration]);

	// Navigation handlers
	const handlePrevStep = useCallback(() => {
		dispatch({ type: "PREV_STEP" });
	}, [dispatch]);

	const handleNextStep = useCallback(() => {
		dispatch({ type: "NEXT_STEP" });
	}, [dispatch]);

	const handleFinish = useCallback(() => {
		onComplete(state.jobs);
	}, [onComplete, state.jobs]);

	// Issue navigation handlers
	const handleNextIssue = useCallback(() => {
		dispatch({ type: "NEXT_ISSUE" });
	}, [dispatch]);

	const handlePrevIssue = useCallback(() => {
		dispatch({ type: "PREV_ISSUE" });
	}, [dispatch]);

	// Unified navigation handlers (combine step + issue navigation)
	const handleNext = useCallback(async () => {
		const hasNoIssues = currentStepIssues.length === 0;

		// Special handling for geo-type-selection step: create jobs before advancing
		if (state.currentStep === "geo-type-selection" && state.phase === "configuration") {
			if (isCreatingJobs) return;

			// Validate that we can proceed
			const { geodataGroups, geoTypeSelections, excludedGroupIds, propertyMappings } = state.configuration;
			const activeGroups = geodataGroups.filter((g) => !excludedGroupIds.has(g.id));
			const allAssigned = activeGroups.every((g) => geoTypeSelections[g.id] != null);
			const hasBoundary = activeGroups.some((g) => geoTypeSelections[g.id] === "Boundary");

			if (!allAssigned || !hasBoundary || activeGroups.length === 0) {
				return;
			}

			setIsCreatingJobs(true);
			try {
				const jobs = await createJobsFromWizard({
					geodataGroups,
					geoTypeSelections,
					excludedGroupIds,
					propertyMappings,
					configuration: state.configuration,
					company: props.company,
					allDeals: props.allDeals,
					salespersons: props.salespersons,
					branches: props.branches,
					selectedBranch: props.selectedBranch,
					growers: props.growers,
					user: props.user,
				});

				if (jobs.length === 0) {
					toast.error("No jobs could be created. Please check your geo-type assignments.");
					return;
				}

				dispatch({ type: "COMPLETE_CONFIGURATION", jobs });
			} catch (error) {
				Oops.handleError(error, "WizardContainerInner_HandleNext_GeoTypeSelection");
			} finally {
				setIsCreatingJobs(false);
			}
			return;
		}

		// Auto-save before advancing on missing-invalid-data step
		if (state.currentStep === "missing-invalid-data" && invalidDataActions?.requestSave) {
			invalidDataActions.requestSave();
		}

		// Standard navigation logic for other steps
		if (hasNoIssues || isLastIssue) {
			dispatch({ type: "NEXT_STEP" });
		} else {
			dispatch({ type: "NEXT_ISSUE" });
		}
	}, [
		currentStepIssues.length,
		isLastIssue,
		dispatch,
		state.currentStep,
		state.phase,
		state.configuration,
		isCreatingJobs,
		invalidDataActions,
		props,
	]);

	const handlePrevious = useCallback(() => {
		const hasNoIssues = currentStepIssues.length === 0;

		if (hasNoIssues || isFirstIssue) {
			// Go to previous step
			dispatch({ type: "PREV_STEP" });
		} else {
			// Go to previous issue within current step
			dispatch({ type: "PREV_ISSUE" });
		}
	}, [currentStepIssues.length, isFirstIssue, dispatch]);

	// Issue skip handlers
	const handleSkipIssue = useCallback(() => {
		if (currentIssue && canSkipCurrentIssue) {
			dispatch({ type: "SKIP_ISSUE", issueId: currentIssue.id });
			// Auto-advance: go to next step if last issue, otherwise next issue
			if (isLastIssue) {
				dispatch({ type: "NEXT_STEP" });
			} else {
				dispatch({ type: "NEXT_ISSUE" });
			}
		}
	}, [dispatch, currentIssue, isLastIssue, canSkipCurrentIssue]);

	const handleSkipAllIssues = useCallback(() => {
		// Skip only skippable issues (those without missing grower)
		const skippableIds = currentStepIssues.filter((i) => !issueHasMissingGrower(i)).map((i) => i.id);
		for (const id of skippableIds) {
			dispatch({ type: "SKIP_ISSUE", issueId: id });
		}
		// Only advance if all issues are now resolved (skipped or unskippable ones don't exist)
		const hasUnskippableRemaining = currentStepIssues.some((i) => issueHasMissingGrower(i));
		if (!hasUnskippableRemaining) {
			dispatch({ type: "NEXT_STEP" });
		}
	}, [dispatch, currentStepIssues]);

	// Job modification handlers
	const handleDeleteJob = useCallback(
		(jobId: string) => {
			dispatch({ type: "DELETE_JOB", jobId });
		},
		[dispatch],
	);

	const handleDeleteJobs = useCallback(
		(jobIds: string[]) => {
			dispatch({ type: "DELETE_JOBS", jobIds });
		},
		[dispatch],
	);

	const handleMergeJobs = useCallback(
		(jobIds: string[], resolutions?: MergeResolutions) => {
			const jobsToMerge = jobIds.map((id) => state.jobs.find((j) => j.id === id)).filter(Boolean) as Job[];
			if (jobsToMerge.length < 2) return;

			try {
				const mergedJob = mergeJobsUtil(jobsToMerge, {
					resolutions,
					skipMergeGeoTypes: ["boundary"],
				});
				const firstIndex = state.jobs.findIndex((j) => jobIds.includes(j.id));
				const idsToRemove = new Set(jobIds);
				const filteredJobs = state.jobs.filter((j) => !idsToRemove.has(j.id));
				filteredJobs.splice(Math.max(firstIndex, 0), 0, mergedJob);

				dispatch({ type: "SET_JOBS", jobs: filteredJobs, preserveStep: true });
			} catch (error) {
				Oops.handleError(error, "WizardContainerInner_HandleMergeJobs");
			}
		},
		[dispatch, state.jobs],
	);

	const handleUpdateJob = useCallback(
		(job: Job) => {
			dispatch({ type: "UPDATE_JOB", job });
		},
		[dispatch],
	);

	// Handler for deleting a feature from a job (for internal duplicates)
	const handleDeleteFeature = useCallback(
		(jobId: string, featureType: "point" | "zone", featureIndex: number) => {
			const job = state.jobs.find((j) => j.id === jobId);
			if (!job) {
				console.error("Cannot delete feature: job not found", jobId);
				return;
			}

			// Immutably update the job's GeoJSON by removing the feature at the specified index
			let updatedJob: Job;
			if (featureType === "point") {
				const pointsGeoJSON = job.files?.pointsGeoJSON;
				if (!pointsGeoJSON?.features) return;
				const newFeatures = pointsGeoJSON.features.filter((_, i) => i !== featureIndex);
				updatedJob = {
					...job,
					files: {
						...job.files,
						pointsGeoJSON: newFeatures.length > 0 ? { ...pointsGeoJSON, features: newFeatures } : undefined,
					},
				};
			} else {
				const zonesGeoJSON = job.files?.zonesGeoJSON;
				if (!zonesGeoJSON?.features) return;
				const newFeatures = zonesGeoJSON.features.filter((_, i) => i !== featureIndex);
				updatedJob = {
					...job,
					files: {
						...job.files,
						zonesGeoJSON: newFeatures.length > 0 ? { ...zonesGeoJSON, features: newFeatures } : undefined,
					},
				};
			}

			// Update the jobs array with preserveStep flag to trigger re-validation but stay on current step
			dispatch({
				type: "SET_JOBS",
				jobs: state.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
				preserveStep: true,
			});
		},
		[dispatch, state.jobs],
	);

	// Handler for deleting multiple features from a job at once (for bulk internal duplicate deletion)
	const handleDeleteFeatures = useCallback(
		(jobId: string, pointIndices: number[], zoneIndices: number[]) => {
			const job = state.jobs.find((j) => j.id === jobId);
			if (!job) {
				console.error("Cannot delete features: job not found", jobId);
				return;
			}

			const pointSet = new Set(pointIndices);
			const zoneSet = new Set(zoneIndices);

			const pointsGeoJSON = job.files?.pointsGeoJSON;
			const zonesGeoJSON = job.files?.zonesGeoJSON;

			const newPointFeatures = pointsGeoJSON?.features?.filter((_, i) => !pointSet.has(i));
			const newZoneFeatures = zonesGeoJSON?.features?.filter((_, i) => !zoneSet.has(i));

			const updatedJob: Job = {
				...job,
				files: {
					...job.files,
					pointsGeoJSON:
						newPointFeatures && newPointFeatures.length > 0
							? { ...pointsGeoJSON!, features: newPointFeatures }
							: pointSet.size > 0
								? undefined
								: pointsGeoJSON,
					zonesGeoJSON:
						newZoneFeatures && newZoneFeatures.length > 0
							? { ...zonesGeoJSON!, features: newZoneFeatures }
							: zoneSet.size > 0
								? undefined
								: zonesGeoJSON,
				},
			};

			dispatch({
				type: "SET_JOBS",
				jobs: state.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
				preserveStep: true,
			});
		},
		[dispatch, state.jobs],
	);

	const handleAssignToJob = useCallback(
		(_sourceJobId: string, _targetJobId: string) => {
			// This would need more complex logic to merge job features
			// For now, skip the current issue and move to next
			const unmatchedIssue = state.issues.unmatched[state.currentIssueIndex];
			if (unmatchedIssue) {
				dispatch({ type: "SKIP_ISSUE", issueId: unmatchedIssue.id });
				dispatch({ type: "NEXT_ISSUE" });
			}
		},
		[dispatch, state.issues.unmatched, state.currentIssueIndex],
	);

	// Pattern for parsing unmatched feature IDs
	const UNMATCHED_FEATURE_ID_PATTERNS = {
		point: /^unmatched-point-(\d+)$/,
		zone: /^unmatched-zone-(\d+)-(\d+)$/,
	};

	const handleSplitFeatures = useCallback(
		(sourceJobId: string, selectedFeatureIds: Set<string>) => {
			// Find the source job in the current jobs list
			const jobIndex = state.jobs.findIndex((j) => j.id === sourceJobId);
			if (jobIndex === -1) {
				console.error("Cannot split: job not found", sourceJobId);
				return;
			}

			const job = state.jobs[jobIndex];
			const unmatchedIssue = state.issues.unmatched.find((i) => i.job.id === sourceJobId);
			if (!unmatchedIssue) {
				console.error("Cannot split: unmatched issue not found for job", sourceJobId);
				return;
			}

			// Parse selected feature IDs to get indices
			const pointIndices = new Set<number>();
			const zoneIndices = new Set<number>();

			for (const featureId of selectedFeatureIds) {
				let match = featureId.match(UNMATCHED_FEATURE_ID_PATTERNS.point);
				if (match) {
					pointIndices.add(parseInt(match[1]));
					continue;
				}
				match = featureId.match(UNMATCHED_FEATURE_ID_PATTERNS.zone);
				if (match) {
					zoneIndices.add(parseInt(match[1]));
					continue;
				}
			}

			// Helper to filter FeatureCollection
			const filterFeatures = (
				collection: FeatureCollection | undefined,
				indices: Set<number>,
				mode: "include" | "exclude",
			): FeatureCollection | undefined => {
				if (!collection?.features) return undefined;
				const filtered = collection.features.filter((_, i) => (mode === "include" ? indices.has(i) : !indices.has(i)));
				if (filtered.length === 0) return undefined;
				return { ...collection, features: filtered };
			};

			// Create new job with selected features
			const newPoints = filterFeatures(job.files?.pointsGeoJSON, pointIndices, "include");
			const newZones = filterFeatures(job.files?.zonesGeoJSON, zoneIndices, "include");

			// Only create new job if there are features to split
			if (!newPoints && !newZones) {
				console.error("Cannot split: no features match selection");
				return;
			}

			// Create new job with selected features
			const newJobId = generateUUID();
			const newJobFiles = {
				...job.files,
				// New job gets the selected features
				pointsGeoJSON: newPoints,
				zonesGeoJSON: newZones,
				// New job has no boundary (these are unmatched features)
				boundaryGeoJSON: undefined,
			};

			const newJobFileOptions = {
				...job.fileOptions,
				pointsOptions: newPoints ? job.fileOptions?.pointsOptions : [],
				zonesOptions: newZones ? job.fileOptions?.zonesOptions : [],
				boundaryOptions: [],
			};

			const newJob: Job = {
				...job,
				id: newJobId,
				files: newJobFiles,
				fileOptions: newJobFileOptions,
			};

			// Update original job to exclude selected features
			const originalPoints = filterFeatures(job.files?.pointsGeoJSON, pointIndices, "exclude");
			const originalZones = filterFeatures(job.files?.zonesGeoJSON, zoneIndices, "exclude");

			const originalJobFiles = {
				...job.files,
				pointsGeoJSON: originalPoints,
				zonesGeoJSON: originalZones,
			};

			const originalJobFileOptions = {
				...job.fileOptions,
				pointsOptions: originalPoints ? job.fileOptions?.pointsOptions : [],
				zonesOptions: originalZones ? job.fileOptions?.zonesOptions : [],
			};

			const originalJob: Job = {
				...job,
				files: originalJobFiles,
				fileOptions: originalJobFileOptions,
			};

			// Update both jobs via dispatch - use preserveStep to stay on current step
			dispatch({
				type: "SET_JOBS",
				jobs: [...state.jobs.map((j) => (j.id === sourceJobId ? originalJob : j)), newJob],
				preserveStep: true,
			});
		},
		[dispatch, state.jobs, state.issues.unmatched],
	);

	const handleUploadBoundary = useCallback(
		async (jobId: string, files: File[]) => {
			// Find the job
			const job = state.jobs.find((j) => j.id === jobId);
			if (!job) {
				Oops.handleError(new Error("Job not found"), "WizardContainerInner_HandleUploadBoundary");
				return;
			}

			try {
				// Parse uploaded files to GeoJSON (GeoData.toGeoJSON auto-detects format)
				const geoJSON = await GeoData.toGeoJSON(files);

				if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
					toast.error("No valid boundary features found in the uploaded file");
					return;
				}

				// Get filename for display (use first file's name without extension)
				const fileName = files[0]?.name.replace(/\.(zip|json|geojson|kml|kmz|shp)$/i, "") || "Uploaded";

				// Create new boundary options group
				const newBoundaryOption = { name: fileName, geoJSON };
				const newBoundaryOptionsGroup: GeoJSONOptionsGroup = {
					type: { type: "Boundary", label: "Uploaded" },
					options: [newBoundaryOption],
				};

				// Extract job field values from boundary properties using wizard mappings
				const extractedFields = extractJobFieldsFromBoundary(geoJSON, state.configuration.propertyMappings);

				// Update job with new boundary and extracted fields
				const existingBoundaryOptions = job.fileOptions?.boundaryOptions || [];
				const filteredBoundaryOptions = existingBoundaryOptions.filter((g) => g.type.label !== "Uploaded");

				const updatedJob: Job = {
					...job,
					...extractedFields,
					files: { ...job.files, boundaryGeoJSON: geoJSON },
					fileOptions: {
						...job.fileOptions,
						boundaryOptions: [...filteredBoundaryOptions, newBoundaryOptionsGroup],
					},
				};

				// Dispatch job update - preserveStep to trigger re-validation but stay on current step
				dispatch({
					type: "SET_JOBS",
					jobs: state.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
					preserveStep: true,
				});
			} catch (error) {
				Oops.handleError(error, "WizardContainerInner_HandleUploadBoundary");
			}
		},
		[dispatch, state.jobs, state.configuration.propertyMappings],
	);

	const handleUploadPoints = useCallback(
		async (jobId: string, files: File[]) => {
			const job = state.jobs.find((j) => j.id === jobId);
			if (!job) {
				Oops.handleError(new Error("Job not found"), "WizardContainerInner_HandleUploadPoints");
				return;
			}

			try {
				const geoJSON = await GeoData.toGeoJSON(files);

				if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
					toast.error("No valid point features found in the uploaded file");
					return;
				}

				const fileName = files[0]?.name.replace(/\.(zip|json|geojson|kml|kmz|shp)$/i, "") || "Uploaded";
				const newOption = { name: fileName, geoJSON };
				const newOptionsGroup: GeoJSONOptionsGroup = {
					type: { type: "Centroids", label: "Uploaded" },
					options: [newOption],
				};

				const existingOptions = job.fileOptions?.pointsOptions || [];
				const filteredOptions = existingOptions.filter((g) => g.type.label !== "Uploaded");

				const updatedJob: Job = {
					...job,
					files: { ...job.files, pointsGeoJSON: geoJSON },
					fileOptions: {
						...job.fileOptions,
						pointsOptions: [...filteredOptions, newOptionsGroup],
					},
				};

				dispatch({
					type: "SET_JOBS",
					jobs: state.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
					preserveStep: true,
				});
			} catch (error) {
				Oops.handleError(error, "WizardContainerInner_HandleUploadPoints");
			}
		},
		[dispatch, state.jobs],
	);

	const handleUploadZones = useCallback(
		async (jobId: string, files: File[]) => {
			const job = state.jobs.find((j) => j.id === jobId);
			if (!job) {
				Oops.handleError(new Error("Job not found"), "WizardContainerInner_HandleUploadZones");
				return;
			}

			try {
				const geoJSON = await GeoData.toGeoJSON(files);

				if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
					toast.error("No valid zone features found in the uploaded file");
					return;
				}

				const fileName = files[0]?.name.replace(/\.(zip|json|geojson|kml|kmz|shp)$/i, "") || "Uploaded";
				const newOption = { name: fileName, geoJSON };
				const newOptionsGroup: GeoJSONOptionsGroup = {
					type: { type: "Zones", label: "Uploaded" },
					options: [newOption],
				};

				const existingOptions = job.fileOptions?.zonesOptions || [];
				const filteredOptions = existingOptions.filter((g) => g.type.label !== "Uploaded");

				const updatedJob: Job = {
					...job,
					files: { ...job.files, zonesGeoJSON: geoJSON },
					fileOptions: {
						...job.fileOptions,
						zonesOptions: [...filteredOptions, newOptionsGroup],
					},
				};

				dispatch({
					type: "SET_JOBS",
					jobs: state.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
					preserveStep: true,
				});
			} catch (error) {
				Oops.handleError(error, "WizardContainerInner_HandleUploadZones");
			}
		},
		[dispatch, state.jobs],
	);

	// ============================================================================
	// Branch Configuration
	// ============================================================================

	// Sync hasBranches flag when branches prop changes
	useEffect(() => {
		const hasBranches = (props.branches?.length ?? 0) > 0;
		dispatch({ type: "SET_HAS_BRANCHES", hasBranches });
	}, [props.branches, dispatch]);

	// Sync isSalespersonRequired flag when company prop changes
	useEffect(() => {
		const isSalespersonRequired = props.company?.portalSettings?.importTableColumns?.salesperson?.required ?? false;
		dispatch({ type: "SET_IS_SALESPERSON_REQUIRED", isSalespersonRequired });
	}, [props.company, dispatch]);

	const handleAssignGrowerToBranch = useCallback(
		(assignments: Record<string, string>) => {
			dispatch({ type: "SET_BRANCH_ASSIGNMENTS", assignments });

			// Update all affected jobs with branch data (assignments keyed by job ID)
			const updatedJobs = state.jobs.map((job) => {
				const branchId = assignments[job.id];
				if (!branchId) return job;
				return { ...job, branchIds: [branchId] };
			});

			const hasChanges = updatedJobs.some((job, i) => job !== state.jobs[i]);
			if (hasChanges) {
				dispatch({ type: "SET_JOBS", jobs: updatedJobs, preserveStep: true });
			}
		},
		[dispatch, state.jobs],
	);

	// ============================================================================
	// Salesperson Assignment Handler
	// ============================================================================

	const handleAssignGrowerToSalesperson = useCallback(
		(assignments: Record<string, string>) => {
			dispatch({ type: "SET_SALESPERSON_ASSIGNMENTS", assignments });

			// Update all affected jobs with salesperson data (assignments keyed by job ID)
			const updatedJobs = state.jobs.map((job) => {
				const spId = assignments[job.id];
				if (!spId) return job;

				// Resolve temp IDs: use CREATE_NEW_SALESPERSON sentinel and set newSalesperson data
				// so addNewSalespersons() can create the real Contact on submission
				if (spId.startsWith("temp-")) {
					const spData = newSalespersonData.get(spId);
					return {
						...job,
						salespersonIds: [CREATE_NEW_SALESPERSON],
						newSalesperson: spData ? { name: spData.name, phone: spData.phone } : undefined,
					};
				}

				return { ...job, salespersonIds: [spId], newSalesperson: undefined };
			});

			const hasChanges = updatedJobs.some((job, i) => job !== state.jobs[i]);
			if (hasChanges) {
				dispatch({ type: "SET_JOBS", jobs: updatedJobs, preserveStep: true });
			}
		},
		[dispatch, state.jobs, newSalespersonData],
	);

	const handleAddSalesperson = useCallback((name: string, phone: string, tempId: string) => {
		// Store temp salesperson data so we can resolve temp IDs to newSalesperson on jobs
		// when they are assigned via handleAssignGrowerToSalesperson
		setNewSalespersonData((prev) => new Map(prev).set(tempId, { name, phone }));
	}, []);

	// ============================================================================
	// Deal Configuration Handler
	// ============================================================================

	const handleAssignGrowerToDeal = useCallback(
		(assignments: Record<string, string>) => {
			dispatch({ type: "SET_DEAL_ASSIGNMENTS", assignments });

			// Update all affected jobs with deal data (assignments keyed by job ID)
			const updatedJobs = state.jobs.map((job) => {
				const dealId = assignments[job.id];
				if (!dealId) return job;

				const deal = props.allDeals?.find((d: Deal) => d.id === dealId);
				if (!deal) return job;

				const updatedJob = { ...job, dealIds: [dealId] };
				Job.updateFromDeal(updatedJob, deal);
				return updatedJob;
			});

			// Only dispatch SET_JOBS if something actually changed
			const hasChanges = updatedJobs.some((job, i) => job !== state.jobs[i]);
			if (hasChanges) {
				dispatch({ type: "SET_JOBS", jobs: updatedJobs, preserveStep: true });
			}
		},
		[dispatch, state.jobs, props.allDeals],
	);

	// ============================================================================
	// Explode/Regroup Handlers
	// ============================================================================

	const handleExplodeGrower = useCallback(
		(growerName: string, step: "deal" | "branch" | "salesperson") => {
			dispatch({ type: "EXPLODE_GROWER", growerName, step });
		},
		[dispatch],
	);

	const handleRegroupGrower = useCallback(
		(growerName: string, step: "deal" | "branch" | "salesperson") => {
			dispatch({ type: "REGROUP_GROWER", growerName, step });
		},
		[dispatch],
	);

	// ============================================================================
	// Configuration Phase Handlers
	// ============================================================================

	// Handler: Update property mappings for a single group
	const handleMappingChange = useCallback(
		(groupId: string, mappings: PropertyMappings) => {
			dispatch({ type: "UPDATE_PROPERTY_MAPPINGS", groupId, mappings });
		},
		[dispatch],
	);

	// Handler: Batch update property mappings for multiple groups at once
	// This avoids N state updates when auto-guessing mappings for all groups
	const handleBatchMappingChange = useCallback(
		(mappings: Record<string, PropertyMappings>) => {
			dispatch({ type: "UPDATE_ALL_PROPERTY_MAPPINGS", mappings });
		},
		[dispatch],
	);

	// Handler: Apply a global mapping field to all groups
	// Uses a dedicated reducer action to avoid stale closure issues — the reducer
	// always operates on the latest state, so auto-guessed mappings won't be lost.
	const handleApplyGlobalMapping = useCallback(
		(field: keyof PropertyMappings, value: string[] | null) => {
			dispatch({ type: "APPLY_GLOBAL_PROPERTY_MAPPING", field, value });
		},
		[dispatch],
	);

	// Handler: Update geo-type for a single group
	const handleGeoTypeChange = useCallback(
		(groupId: string, geoType: GeoType | null) => {
			dispatch({ type: "UPDATE_GEO_TYPE", groupId, geoType });
		},
		[dispatch],
	);

	// Handler: Apply geo-type to all groups of a geometry type
	const handleApplyToAllGeoType = useCallback(
		(geometryType: "polygon" | "point", geoType: GeoType) => {
			const updates: Record<string, GeoType | null> = {};
			for (const group of state.configuration.geodataGroups) {
				if (!state.configuration.excludedGroupIds.has(group.id) && group.geometryType === geometryType) {
					updates[group.id] = geoType;
				}
			}
			dispatch({ type: "UPDATE_ALL_GEO_TYPES", updates });
		},
		[dispatch, state.configuration.geodataGroups, state.configuration.excludedGroupIds],
	);

	// Handler: Apply geo-type to selected groups (only applies to compatible geometry types)
	const handleApplyToSelectedGeoType = useCallback(
		(geoType: GeoType) => {
			const targetGeomType = geoType === "Boundary" || geoType === "Zones" ? "polygon" : "point";
			const updates: Record<string, GeoType | null> = {};
			for (const groupId of state.configuration.selectedGroupIdsForMap) {
				const group = state.configuration.geodataGroups.find((g) => g.id === groupId);
				if (group && group.geometryType === targetGeomType) {
					updates[groupId] = geoType;
				}
			}
			dispatch({ type: "UPDATE_ALL_GEO_TYPES", updates });
		},
		[dispatch, state.configuration.selectedGroupIdsForMap, state.configuration.geodataGroups],
	);

	// Handler: Exclude group from processing
	const handleExcludeGroup = useCallback(
		(groupId: string) => {
			dispatch({ type: "EXCLUDE_GROUP", groupId });
		},
		[dispatch],
	);

	// Handler: Include previously excluded group
	const handleIncludeGroup = useCallback(
		(groupId: string) => {
			dispatch({ type: "INCLUDE_GROUP", groupId });
		},
		[dispatch],
	);

	// Handler: Update map selection
	const handleMapSelectionChange = useCallback(
		(groupIds: string[]) => {
			dispatch({ type: "SET_SELECTED_GROUPS_FOR_MAP", groupIds });
		},
		[dispatch],
	);

	// Handler: Complete configuration phase and create jobs
	const handleCompleteConfiguration = useCallback(
		async (jobs: Job[]) => {
			dispatch({ type: "COMPLETE_CONFIGURATION", jobs });
		},
		[dispatch],
	);

	// ============================================================================
	// File Management Handlers (FileValidation step)
	// ============================================================================

	// Handler: Add more files
	const handleAddFiles = useCallback(() => {
		try {
			// Create file input
			const fileInput = document.createElement("input");
			fileInput.type = "file";
			fileInput.accept = ".zip,.json,.geojson,.kml,.kmz,.shp,.shx,.dbf,.prj";
			fileInput.multiple = true;
			fileInput.onchange = async (event) => {
				event.preventDefault();

				// @ts-ignore
				const newFiles: File[] = Array.from(event.target.files);
				if (newFiles.length === 0) return;

				// Merge with existing files (replace files with same name)
				const oldFiles = [...state.configuration.originalFiles];
				for (const newFile of newFiles) {
					const existingIndex = oldFiles.findIndex((oldFile) => oldFile.name === newFile.name);
					if (existingIndex !== -1) {
						oldFiles.splice(existingIndex, 1);
					}
				}
				const allFiles = [...oldFiles, ...newFiles];

				// Process files
				const unzippedFiles: File[] = [];
				const zippedFiles: File[] = [];

				for (const file of allFiles) {
					const lowerName = file.name.toLowerCase();
					if (lowerName.endsWith(".zip")) {
						zippedFiles.push(file);
					} else {
						unzippedFiles.push(file);
					}
				}

				// Process shapefile components
				const fileMap = await GeoData.extractGroupedFiles(zippedFiles);

				// Add standalone files to fileMap
				for (const file of unzippedFiles) {
					fileMap[file.name] = [file];
				}

				const allFileNames = await extractAllFileNames(allFiles);
				const unsupportedWarnings = detectUnsupportedFiles(allFileNames);

				const parsingWarnings: FileValidationWarning[] = [];

				// Process files and create GeoJSONData objects
				const geoDataEntries = await Promise.all(
					Object.keys(fileMap).map(async (fullPath) => {
						const geoFiles = fileMap[fullPath];
						// Only append .zip for shapefile groups
						if (isShapefile(fullPath) && !fullPath.endsWith(".zip")) {
							fullPath = fullPath + ".zip";
						}

						try {
							const geoJSON = await GeoData.toGeoJSON(geoFiles, undefined, fullPath);

							// Check for empty or invalid GeoJSON
							const warning = categorizeGeoDataResult(fullPath, geoJSON);
							if (warning) {
								parsingWarnings.push(warning);
								return [fullPath, null] as const;
							}

							// Extract property names
							const propertyNames = GeoData.extractPropertyNames(geoJSON);
							const geoTypes = GeoData.countGeoTypes(geoJSON);
							let geometryTypes = Object.keys(geoTypes);

							// Normalize MultiPolygon to Polygon
							if (geometryTypes.includes("MultiPolygon")) {
								geometryTypes[geometryTypes.indexOf("MultiPolygon")] = "Polygon";
								geometryTypes = Array.from(new Set(geometryTypes));
							}

							// Create group key
							const folderLevels = fullPath.split("/");
							const groupKey = `${folderLevels.length},${propertyNames.join(",")},${geometryTypes.join(",")}`;

							// Create GeoJSONData object
							const geoData: GroupedGeoJSON = {
								groupKey: fullPath,
								geoJSON,
								propertyNames,
								geometryTypes: geometryTypes as any[],
								originalFile: new File([new Uint8Array(await convertFilesIntoBuffer(geoFiles))], fullPath),
							};

							return [groupKey, geoData] as const;
						} catch (error) {
							const warning = categorizeGeoDataResult(
								fullPath,
								null,
								error instanceof Error ? error : new Error(String(error)),
							);
							if (warning) {
								parsingWarnings.push(warning);
							}
							return [fullPath, null] as const;
						}
					}),
				);

				// Create new GeoJSONDataMap
				const newGeoDataMap: GeoJSONDataMap = new Map();

				for (const [groupKey, geoData] of geoDataEntries) {
					if (!geoData) continue;
					if (!newGeoDataMap.has(groupKey)) {
						newGeoDataMap.set(groupKey, []);
					}
					newGeoDataMap.get(groupKey)?.push(geoData);
				}

				// Create geodata groups from the map
				const geodataGroups: GeodataGroup[] = Array.from(newGeoDataMap.entries()).map(([groupKey, geoDataList]) => {
					const allFeatures = geoDataList.flatMap((d) => d.geoJSON.features);
					const firstFeature = allFeatures[0];
					const geomType = firstFeature?.geometry?.type;
					const isPoint = geomType === "Point" || geomType === "MultiPoint";
					const geometryType: "polygon" | "point" = isPoint ? "point" : "polygon";

					const propertyNames = Array.from(
						new Set(allFeatures.flatMap((f) => (f.properties ? Object.keys(f.properties) : []))),
					);

					const combinedGeoJSON: FeatureCollection = {
						type: "FeatureCollection",
						features: allFeatures,
					};

					return {
						id: groupKey,
						displayName: groupKey.split("/").pop() || groupKey,
						filePaths: geoDataList.map((d) => d.groupKey),
						geometryType,
						featureCount: allFeatures.length,
						propertyNames,
						geoJSON: combinedGeoJSON,
					};
				});

				// Create uploaded files info
				const uploadedFiles: UploadedFileInfo[] = allFiles.map((file) => ({
					name: file.name,
					path: file.name,
					size: file.size,
				}));

				// All warnings
				const allWarnings = [...unsupportedWarnings, ...parsingWarnings];

				// Dispatch update
				dispatch({
					type: "UPDATE_GEODATA",
					geodata: {
						warnings: allWarnings,
						uploadedFiles,
						geodataGroups,
						geoDataMap: newGeoDataMap,
						selectableGeoData: {},
						originalFiles: allFiles,
						// Reset these when files change
						propertyMappings: {},
						geoTypeSelections: {},
						excludedGroupIds: new Set(),
						selectedGroupIdsForMap: [],
					},
				});
			};
			fileInput.click();
		} catch (error) {
			Oops.handleError(error, "WizardContainerInner.handleAddFiles");
		}
	}, [dispatch, state.configuration.originalFiles]);

	// Handler: Clear all files
	const handleClearFiles = useCallback(() => {
		dispatch({ type: "CLEAR_GEODATA" });
	}, [dispatch]);

	// Handler: Navigate directly to a specific step (backward navigation from sidebar)
	const handleGoToStep = useCallback(
		(step: WizardStep) => {
			dispatch({ type: "GO_TO_STEP", step });
		},
		[dispatch],
	);

	// Handler: Navigate to a specific step and issue index (for Summary skipped-issue click)
	const handleNavigateToIssue = useCallback(
		(step: WizardStep, issueIndex: number) => {
			dispatch({ type: "GO_TO_STEP", step, issueIndex });
		},
		[dispatch],
	);

	// Handler: Start Over - reset wizard to initial state and go back to file-validation
	const handleStartOver = useCallback(() => {
		// Clear geodata and reset wizard state
		dispatch({ type: "CLEAR_GEODATA" });
		// Go back to file-validation step
		dispatch({ type: "GO_TO_STEP", step: "file-validation" });
		// Call parent's onStartOver if provided (to clear parent state)
		props.onStartOver?.();
	}, [dispatch, props.onStartOver]);

	return {
		phase: state.phase,
		currentStep: state.currentStep,
		configuration: state.configuration,
		isValidating: state.isValidating,
		validationError: state.validationError,
		visibleSteps,
		stepIssueCounts,
		completedSteps: state.completedSteps,
		visitedSteps: state.visitedSteps,
		resolvedSteps,
		currentStepIssues,
		currentIssue,
		currentIssueIndex: state.currentIssueIndex,
		isFirstStep,
		isLastStep,
		isFirstIssue,
		isLastIssue,
		canProceed: canProceedToNextStep,
		canClickNext,
		canClickPrevious,
		disabledReason,
		geoTypeStatus,
		isCurrentIssueSkipped,
		unskippedCount,
		canSkipCurrentIssue,
		skippableUnskippedCount,
		invalidDataActions,
		jobs: state.jobs,
		totalJobCount: state.jobs.length,
		issues: state.issues,
		skippedIssueIds: state.skippedIssueIds,
		setInvalidDataActions,
		handlePrevStep,
		handleNextStep,
		handleNext,
		handlePrevious,
		handleFinish,
		handleNextIssue,
		handlePrevIssue,
		handleSkipIssue,
		handleSkipAllIssues,
		handleDeleteJob,
		handleDeleteJobs,
		handleMergeJobs,
		handleUpdateJob,
		handleDeleteFeature,
		handleDeleteFeatures,
		handleAssignToJob,
		handleSplitFeatures,
		handleUploadBoundary,
		handleUploadPoints,
		handleUploadZones,
		// Configuration phase handlers
		handleMappingChange,
		handleBatchMappingChange,
		handleApplyGlobalMapping,
		handleGeoTypeChange,
		handleApplyToAllGeoType,
		handleApplyToSelectedGeoType,
		handleExcludeGroup,
		handleIncludeGroup,
		handleMapSelectionChange,
		handleCompleteConfiguration,
		// File management handlers
		handleAddFiles,
		handleClearFiles,
		// Start over handler
		handleStartOver,
		// Go to step handler (sidebar click)
		handleGoToStep,
		// Navigate to specific issue (Summary skipped-issue click)
		handleNavigateToIssue,
		// Deal configuration
		dealAssignments: state.dealAssignments,
		handleAssignGrowerToDeal,
		// Branch configuration
		branchAssignments: state.branchAssignments,
		handleAssignGrowerToBranch,
		// Salesperson assignment
		salespersonAssignments: state.salespersonAssignments,
		handleAssignGrowerToSalesperson,
		handleAddSalesperson,
		// Salesperson required flag
		isSalespersonRequired: state.isSalespersonRequired,
		// Explode/regroup
		explodedGrowers: state.explodedGrowers,
		handleExplodeGrower,
		handleRegroupGrower,
	};
}
